import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Edit from '@mui/icons-material/Edit';
import PageHeader from '../../../Components/PageHeader';
import TablePeo from '../../../Components/TablePeo';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useApiGet } from '../../../utils/httpClientTs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listInventory,
  adjustInventory,
  kitAssembly,
  listOnlineItems,
  listInventoryTransactions,
} from '../../../endpoints/chapterSupplies';
import type {
  InventoryResponse,
  InventoryTransactionResponse,
} from '../../../types/chapterSupplies';
import type { OnlineItemResponse } from '../../../types/chapterSupplies';
import type { TableHeadCell } from '../../../types/table';

const headCells: TableHeadCell[] = [
  { id: 'item_name', firstCell: true, label: 'Item name', width: '30%' },
  { id: 'item_id', label: 'Item ID', width: '22%' },
  { id: 'quantity_on_hand', label: 'Quantity on hand', width: '18%' },
  {
    id: 'updated_at',
    label: 'Updated',
    width: '18%',
    format: (v: unknown): React.ReactNode => {
      if (!v) return '—';
      const d = new Date(v as string);
      if (Number.isNaN(d.getTime()) || d.getFullYear() < 1970) return '—';
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
  {
    id: 'actions',
    label: 'Actions',
    width: '12%',
    sortable: false,
    format: (v: unknown): React.ReactNode => v as React.ReactNode,
  },
];

export default function InventoryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('updated_at');

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustRow, setAdjustRow] = useState<InventoryResponse | null>(null);
  const [quantityDelta, setQuantityDelta] = useState<string>('0');

  const [kitOnlineItemId, setKitOnlineItemId] = useState<string>('');
  const [kitQuantity, setKitQuantity] = useState<string>('1');
  const [kitError, setKitError] = useState<string>('');
  const [kitSuccess, setKitSuccess] = useState(false);

  const limit = rowsPerPage;
  const offset = page * rowsPerPage;

  const { data: inventoryData, isFetching } = useApiGet(
    ['chapter-supplies-inventory', limit, offset],
    () => listInventory({ limit, offset }),
    { enabled: true, retry: 1 }
  );

  const { data: onlineItemsData } = useApiGet(
    ['chapter-supplies-online-items-kits', 500, 0],
    () => listOnlineItems({ limit: 500, offset: 0 }),
    { enabled: true }
  );

  const { data: transactionsData, isFetching: transactionsFetching } =
    useApiGet(
      ['chapter-supplies-inventory-transactions', 20, 0],
      () => listInventoryTransactions({ limit: 20, offset: 0 }),
      { enabled: true }
    );

  const inventoryList = inventoryData?.inventory ?? [];
  const transactionList = transactionsData?.transactions ?? [];
  const kitOptions = (onlineItemsData?.online_items ?? []).filter(
    (o: OnlineItemResponse) => o.item_type === 'kit'
  );

  const adjustMutation = useMutation({
    mutationFn: async ({ itemId, delta }: { itemId: string; delta: number }) =>
      adjustInventory(itemId, { quantity_delta: delta }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['chapter-supplies-inventory'],
      });
      queryClient.invalidateQueries({
        queryKey: ['chapter-supplies-inventory-transactions'],
      });
      setAdjustOpen(false);
      setAdjustRow(null);
      setQuantityDelta('0');
    },
  });

  const kitAssemblyMutation = useMutation({
    mutationFn: (data: { online_item_id: string; quantity: number }) =>
      kitAssembly(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['chapter-supplies-inventory'],
      });
      queryClient.invalidateQueries({
        queryKey: ['chapter-supplies-inventory-transactions'],
      });
      setKitError('');
      setKitSuccess(true);
      setKitQuantity('1');
      setTimeout(() => setKitSuccess(false), 3000);
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      setKitError(err?.response?.data?.error ?? 'Kit assembly failed');
    },
  });

  const handleAdjustClick = (row: InventoryResponse) => {
    setAdjustRow(row);
    setQuantityDelta('0');
    setAdjustOpen(true);
  };

  const handleAdjustSubmit = () => {
    if (!adjustRow) return;
    const delta = parseInt(quantityDelta, 10);
    if (Number.isNaN(delta)) return;
    adjustMutation.mutate({ itemId: adjustRow.item_id, delta });
  };

  const handleKitAssemblySubmit = () => {
    setKitError('');
    if (!kitOnlineItemId) {
      setKitError('Select a kit');
      return;
    }
    const qty = parseInt(kitQuantity, 10);
    if (Number.isNaN(qty) || qty < 1) {
      setKitError('Enter a valid quantity (at least 1)');
      return;
    }
    kitAssemblyMutation.mutate({
      online_item_id: kitOnlineItemId,
      quantity: qty,
    });
  };

  const rows = inventoryList.map((inv: InventoryResponse) => ({
    ...inv,
    id: inv.id,
    item_name: inv.item_name ?? inv.item_id ?? '—',
    actions: (
      <Button
        size="small"
        startIcon={<Edit />}
        onClick={(e) => {
          e.stopPropagation();
          handleAdjustClick(inv);
        }}
      >
        Adjust
      </Button>
    ),
  }));

  return (
    <Grid container paddingLeft={2} paddingRight={2} spacing={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Inventory"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management')}
            >
              Back
            </Button>
          }
          rightDom={undefined}
          subtitle={undefined}
        />
      </Grid>

      <Grid item xs={12} md={8}>
        <TablePeo
          data={rows}
          isFetching={isFetching}
          headCells={headCells}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          emptyMessage="No items yet."
        />
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Transaction history
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recent inventory transactions (Receive, Adjust, Sale, Kit Assembly).
          </Typography>
          <TablePeo
            data={transactionList.map((tx: InventoryTransactionResponse) => ({
              ...tx,
              id: tx.id,
              created_at: tx.created_at,
              item_name: tx.item_name ?? tx.item_id ?? '—',
              transaction_type_name: tx.transaction_type_name ?? '—',
              quantity: tx.quantity,
            }))}
            isFetching={transactionsFetching}
            headCells={[
              {
                id: 'created_at',
                firstCell: true,
                label: 'Date',
                width: '22%',
                format: (v: unknown) =>
                  v
                    ? new Date(v as string).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—',
              },
              { id: 'item_name', label: 'Item', width: '28%' },
              { id: 'transaction_type_name', label: 'Type', width: '22%' },
              {
                id: 'quantity',
                label: 'Quantity',
                width: '18%',
                format: (v: unknown) =>
                  typeof v === 'number' ? (v >= 0 ? `+${v}` : String(v)) : '—',
              },
            ]}
            page={0}
            setPage={() => {}}
            rowsPerPage={20}
            setRowsPerPage={() => {}}
            order="desc"
            setOrder={() => {}}
            orderBy="created_at"
            setOrderBy={() => {}}
            emptyMessage="No transactions yet."
          />
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Kit assembly
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assemble kits by consuming component item inventory.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="kit-select-label">Kit</InputLabel>
            <Select
              labelId="kit-select-label"
              value={kitOnlineItemId}
              label="Kit"
              onChange={(e) => setKitOnlineItemId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a kit</em>
              </MenuItem>
              {kitOptions.map((o: OnlineItemResponse) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name ?? o.item_name ?? o.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Quantity to assemble"
            value={kitQuantity}
            onChange={(e) => setKitQuantity(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ mb: 2 }}
          />
          {kitError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {kitError}
            </Typography>
          )}
          {kitSuccess && (
            <Typography color="success.main" variant="body2" sx={{ mb: 1 }}>
              Kit assembly completed.
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleKitAssemblySubmit}
            disabled={kitAssemblyMutation.isPending || !kitOnlineItemId}
          >
            {kitAssemblyMutation.isPending ? 'Assembling…' : 'Assemble kits'}
          </Button>
        </Paper>
      </Grid>

      <Dialog
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust inventory</DialogTitle>
        <DialogContent>
          {adjustRow && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {adjustRow.item_name ?? adjustRow.item_id}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Current quantity on hand: {adjustRow.quantity_on_hand}
              </Typography>
              <TextField
                fullWidth
                label="Quantity change (+ or -)"
                type="number"
                value={quantityDelta}
                onChange={(e) => setQuantityDelta(e.target.value)}
                helperText="e.g. 5 to add 5, -3 to subtract 3"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdjustSubmit}
            disabled={
              adjustMutation.isPending ||
              !adjustRow ||
              Number.isNaN(parseInt(quantityDelta, 10))
            }
          >
            {adjustMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
